// Text helpers shared by the engine (browser) and the runner (Node). Pure: no browser APIs.

/** Collapse runs of HTML whitespace to one space and trim. U+00A0 is left alone (Pitfall M8). */
export function normalize(text: string | undefined | null): string {
  if (!text) return '';
  return text.replace(/[ \t\n\r\f]+/g, ' ').replace(/^ | $/g, '');
}

/** Words are runs of characters between U+0020 spaces (after normalization). */
export function words(text: string): string[] {
  return text.split(' ').filter(w => w.length > 0);
}

export function codePointLength(text: string): number {
  let n = 0;
  for (const _ of text) n++;
  return n;
}

/** UTF-16 offset of code point number `index` (0-based). Returns text.length when out of range. */
export function codePointOffset(text: string, index: number): number {
  let n = 0;
  let off = 0;
  for (const ch of text) {
    if (n === index) return off;
    off += ch.length;
    n++;
  }
  return text.length;
}

export function trimEndOffset(text: string, end: number): number {
  while (end > 0 && text.charCodeAt(end - 1) === 0x20) end--;
  return end;
}

/**
 * Percentage of words fully shown: a word counts when it lies entirely before `shownEnd`.
 * 100 for text with no words.
 */
export function shownPct(original: string, shownEnd: number): number {
  let total = 0;
  let shown = 0;
  let i = 0;
  const n = original.length;
  while (i < n) {
    while (i < n && original.charCodeAt(i) === 0x20) i++;
    if (i >= n) break;
    let j = i;
    while (j < n && original.charCodeAt(j) !== 0x20) j++;
    total++;
    if (j <= shownEnd) shown++;
    i = j;
  }
  return total === 0 ? 100 : Math.round((100 * shown) / total);
}

const WORD_CHAR = /[\p{L}\p{N}\p{M}]/u;

function isWordChar(ch: string | undefined): boolean {
  return ch !== undefined && WORD_CHAR.test(ch);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface Occurrence {
  start: number;
  end: number;
}

/**
 * Whole-word, case-insensitive occurrences of `needle` in `text`.
 * Whitespace in the needle matches one space (text is normalized).
 */
export function findOccurrences(text: string, needle: string): Occurrence[] {
  const n = normalize(needle);
  if (!n) return [];
  const pattern = n.split(' ').map(escapeRegExp).join(' ');
  const re = new RegExp(pattern, 'giu');
  const out: Occurrence[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    // A boundary is only required where the needle itself starts/ends with a word character.
    const prev = start > 0 ? lastCodePoint(text.slice(Math.max(0, start - 2), start)) : undefined;
    const next = end < text.length ? String.fromCodePoint(text.codePointAt(end)!) : undefined;
    const startOk = !isWordChar(firstCodePoint(m[0])) || !isWordChar(prev);
    const endOk = !isWordChar(lastCodePoint(m[0])) || !isWordChar(next);
    if (startOk && endOk) out.push({ start, end });
    if (m[0].length === 0) re.lastIndex++;
    else re.lastIndex = start + 1;
  }
  return out;
}

function firstCodePoint(s: string): string | undefined {
  const cp = s.codePointAt(0);
  return cp === undefined ? undefined : String.fromCodePoint(cp);
}

function lastCodePoint(s: string): string | undefined {
  if (!s) return undefined;
  const arr = Array.from(s);
  return arr[arr.length - 1];
}

/** True when `needle` occurs in `text` and at least one occurrence lies fully within [0, shownEnd). */
export function occurrenceShown(occ: Occurrence[], shownEnd: number): boolean {
  return occ.some(o => o.end <= shownEnd);
}

/**
 * The connector the shown text ends on, or null. A last word equal to one of `connectors`
 * (case-insensitive), or a last character that is a punctuation connector (`Pricing:`, `Cards &`).
 */
export function danglingConnector(shownText: string, connectors: string[]): string | null {
  const t = shownText.replace(/ +$/, '');
  if (!t) return null;
  const ws = words(t);
  const last = ws[ws.length - 1];
  const lower = last.toLowerCase();
  for (const c of connectors) if (c.toLowerCase() === lower) return last;
  const lastChar = lastCodePoint(t)!;
  const punctConnectors = connectors.filter(c => c.length === 1 && !isWordChar(c));
  if (punctConnectors.includes(lastChar)) return lastChar;
  return null;
}

export interface BoldRun {
  start: number;
  end: number;
}

/**
 * Words of `text` that Google would bold for `query` (approximation, §4.8).
 * Returns merged runs (adjacent bold words separated by a single space merge into one run).
 */
export function boldRuns(text: string, query: string | undefined, stopwords: string[]): BoldRun[] {
  if (!query) return [];
  const stop = new Set(stopwords.map(s => s.toLowerCase()));
  const terms = Array.from(
    new Set(
      normalize(query)
        .split(' ')
        .map(t => t.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase())
        .filter(t => codePointLength(t) > 1 && !stop.has(t)),
    ),
  ).sort((a, b) => b.length - a.length);
  if (terms.length === 0) return [];
  const taken = new Uint8Array(text.length);
  const runs: BoldRun[] = [];
  for (const term of terms) {
    for (const o of findOccurrences(text, term)) {
      let free = true;
      for (let i = o.start; i < o.end; i++) if (taken[i]) { free = false; break; }
      if (!free) continue;
      for (let i = o.start; i < o.end; i++) taken[i] = 1;
      runs.push(o);
    }
  }
  runs.sort((a, b) => a.start - b.start);
  const merged: BoldRun[] = [];
  for (const r of runs) {
    const prev = merged[merged.length - 1];
    if (prev && r.start === prev.end + 1 && text[prev.end] === ' ') prev.end = r.end;
    else if (prev && r.start === prev.end) prev.end = r.end;
    else merged.push({ ...r });
  }
  return merged;
}

/** Split `text` into items for rich-inline layout. Bold items use `boldFont`. */
export function richItems(text: string, runs: BoldRun[], font: string, boldFont: string): { text: string; font: string; start: number }[] {
  const items: { text: string; font: string; start: number }[] = [];
  let pos = 0;
  for (const r of runs) {
    if (r.start > pos) items.push({ text: text.slice(pos, r.start), font, start: pos });
    items.push({ text: text.slice(r.start, r.end), font: boldFont, start: r.start });
    pos = r.end;
  }
  if (pos < text.length) items.push({ text: text.slice(pos), font, start: pos });
  return items;
}
